import React, { ReactElement, useState } from 'react';
import {
  AppErrorCode,
  ManuallyEnterCardNumberForPrintJobMutationVariables,
  useManuallyEnterCardNumberForPrintJobMutation,
} from 'apollo/generated/client-operations';
import { useIsMounted } from 'utils';
import { getGraphQLError, getGraphQLErrorByCode } from 'utils/error';
import { ArrowRightIcon } from 'components/icons';
import { AlertService } from 'components/alertService';
import { FormOnSubmit, ModalFormContent } from 'components/form';
import { CardPrintJob } from 'containers/worker/types';
import { useProximityCardFormatOptions } from 'containers/worker/helpers/useProximityCardFormatOptions';
import {
  ReassignBadgeState,
  WorkerCardAlreadyExistsErrorExtensions,
} from 'containers/worker/modals/badgeModal/components/types';
import {
  getFormInputsHook,
  getFormDefaultValues,
} from './EnterPrintJobCardNumberModalContent.forms';
import { CreateBadgeInfo } from './types';

const instructions =
  'Enter the badge number printed on the back of the card near the bottom edge.';

export interface EnterPrintJobCardNumberModalContentProps {
  setOpen: () => Promise<void> | void;
  onAction: (reassignBadgeState?: ReassignBadgeState) => void;
  jobsiteId: string;
  cardPrintJob: CardPrintJob;
  refetchCardPrintJob: () => Promise<unknown>;
}

export function EnterPrintJobCardNumberModalContent(
  props: EnterPrintJobCardNumberModalContentProps,
): ReactElement {
  const { setOpen, onAction, cardPrintJob, refetchCardPrintJob, jobsiteId } =
    props;

  const isMounted = useIsMounted();
  const [isFetching, setIsFetching] = useState<boolean>(false);

  const [manuallyEnterCardNumberForPrintJob] =
    useManuallyEnterCardNumberForPrintJobMutation();

  const onSubmit: FormOnSubmit<CreateBadgeInfo> = async (
    data,
    event,
    dirtyFields,
    formApi,
  ): Promise<void> => {
    if (isFetching) return;
    setIsFetching(true);

    const getVariables = (options?: {
      forceCreate?: boolean;
    }): ManuallyEnterCardNumberForPrintJobMutationVariables => ({
      forceCreate: options?.forceCreate,
      cardPrintJobId: cardPrintJob.cardPrintJobId,
      cardNumber: data.badgeNumber?.trim(),
      workerCardFormatId: data.proximityCardFormatId.value,
    });

    try {
      await manuallyEnterCardNumberForPrintJob({ variables: getVariables() });
      await refetchCardPrintJob();
      onAction();
    } catch (error) {
      event.preventDefault();

      const workerCardAlreadyExistsError =
        getGraphQLErrorByCode<WorkerCardAlreadyExistsErrorExtensions>(
          error,
          AppErrorCode.WorkerCardAlreadyExists,
        );
      if (workerCardAlreadyExistsError) {
        const { scope, workerCardInfo } =
          workerCardAlreadyExistsError.extensions ?? {};

        if (scope === 'onDifferentWorker') {
          onAction({
            workerCardInfo,
            reassignBadge: async (): Promise<void> => {
              await manuallyEnterCardNumberForPrintJob({
                variables: getVariables({ forceCreate: true }),
              });
              await refetchCardPrintJob();
            },
          });
        } else {
          formApi.setError('badgeNumber', {
            message: workerCardAlreadyExistsError.message,
            shouldFocus: true,
          });
        }
      } else {
        AlertService.alert(
          'danger',
          'Something went wrong!',
          getGraphQLError(error),
        );
      }
    } finally {
      if (isMounted()) {
        setIsFetching(false);
      }
    }
  };

  const {
    proximityCardFormatOptions,
    defaultProximityCardFormatOption,
    error: getJobsiteCardFormatsError,
  } = useProximityCardFormatOptions(
    cardPrintJob.jobsite?.jobsiteId ?? jobsiteId,
  );

  const formInputs = React.useMemo(
    () => getFormInputsHook({ proximityCardFormatOptions }),
    [proximityCardFormatOptions],
  );
  const defaultValues = React.useMemo(
    () =>
      getFormDefaultValues({
        proximityCardFormatOptions,
        defaultProximityCardFormatOption,
      }),
    [proximityCardFormatOptions, defaultProximityCardFormatOption],
  );

  return (
    <ModalFormContent
      setOpen={setOpen}
      renderAbove={<p className="-odin-mt-3 odin-mb-3">{instructions}</p>}
      inputs={formInputs}
      onSubmit={onSubmit}
      defaultValues={defaultValues}
      inputsContainerClassName="odin-grid odin-grid-cols-6 odin-gap-6"
      actionText="Continue"
      actionIcon={ArrowRightIcon}
      actionButtonWithSpinner={isFetching}
      error={getJobsiteCardFormatsError}
    />
  );
}
